/**
 * Быстрые ответы: папки, заготовки и доступ к папкам — в одном месте.
 *
 * ── Что здесь изменилось ────────────────────────────────────────────────────
 *
 * Было две панели рядом. Слева «Быстрые ответы»: форма из трёх полей, всегда
 * раскрытая, а под ней вперемешку все заготовки всех папок. Справа «Папки»:
 * список папок со своими стрелками и выдачей доступа. Общего у них было только
 * слово «папка», и понять, что список слева как-то связан со списком справа,
 * можно было лишь опытным путём.
 *
 * Хуже всего было создание. Форма стояла первой на экране и в режиме правки
 * ничем не отличалась от режима создания: правя чужую заготовку, легко было
 * решить, что заводишь новую, — и вместо новой переписать существующую, общую
 * для всей компании.
 *
 * Теперь слева папки, справа заготовки выбранной папки. Создание и правка — в
 * окне, у которого есть заголовок, и перепутать их нельзя.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UserDto, UserSummaryDto } from "@tw/shared-types";
import {
  type AccountFolderDto,
  type QuickReplyDto,
  type QuickReplyFolderDto,
} from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { describeError } from "../lib/errors.js";
import { AccessChips } from "./AccessChips.tsx";
import { ADMIN_PANEL, ADMIN_PANEL_TITLE } from "./adminPanel.js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FIELD } from "./controls.js";
import {
  ChevronIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "./icons.tsx";
import { cn } from "@/lib/utils";
import { RichText } from "./RichText.tsx";
import { RichTextInput, type RichTextInputHandle, type RichValue } from "./RichTextInput.tsx";
import { EmojiPicker } from "./EmojiPicker.tsx";
import type { TelegramTextEntityDto } from "@tw/shared-types";
import { QuickReplyThumbs } from "./QuickReplyThumbs.tsx";

type AnyUser = UserDto | UserSummaryDto;

/** Показать все заготовки разом. */
const SCOPE_ALL = "__all__";
/** Заготовки без папки — общие, их видят все. */
const SCOPE_COMMON = "__common__";

export function QuickRepliesPanel({ users }: { users: AnyUser[] }) {
  const { api, can } = useSession();

  const [replies, setReplies] = useState<QuickReplyDto[]>([]);
  const [folders, setFolders] = useState<QuickReplyFolderDto[]>([]);
  const [teams, setTeams] = useState<AccountFolderDto[]>([]);

  const [scope, setScope] = useState<string>(SCOPE_ALL);
  // Аккаунт для пикера премиальных эмодзи в редакторе (наборы берутся с него).
  const [emojiAccountId, setEmojiAccountId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<QuickReplyDto | "new" | null>(null);
  /**
   * Папка, открытая в окне настроек, — по ИДЕНТИФИКАТОРУ, а не объектом.
   *
   * Здесь лежал сам объект папки, взятый в момент открытия, и это был
   * настоящий баг: выдача доступа уходила на сервер и сохранялась, список
   * папок перечитывался, но окно продолжало показывать снимок со старым
   * составом выдач. Со стороны это выглядело так, будто доступ не выдаётся
   * вовсе, — нажимаешь на команду, и ничего не происходит.
   *
   * Идентификатор такого разлада не допускает: окно каждый раз берёт папку из
   * свежего списка.
   */
  const [folderSettingsId, setFolderSettingsId] = useState<string | null>(null);
  const [addingFolder, setAddingFolder] = useState(false);

  const canManage = can("quickreply.manage");

  const load = useCallback(async () => {
    try {
      const [list, folderList] = await Promise.all([
        api.listQuickReplies(),
        api.quickReplyFolders(),
      ]);
      setReplies(list.items);
      setFolders(folderList.items);
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    }
  }, [api]);

  useEffect(() => {
    if (canManage) void load();
  }, [canManage, load]);

  useEffect(() => {
    if (!canManage) return;
    void api
      .listAccounts()
      .then(({ items }) => {
        const active = items.find((a) => a.connectionState === "CONNECTED") ?? items[0];
        setEmojiAccountId(active?.id ?? null);
      })
      .catch(() => setEmojiAccountId(null));
  }, [api, canManage]);

  useEffect(() => {
    if (!canManage) return;
    api
      .accountFolders()
      // Команды нужны только выдаче доступа. Недоступное дерево не должно
      // ломать экран: выдать доступ команде просто будет нечему.
      .then((tree) => setTeams(tree.folders))
      .catch(() => setTeams([]));
  }, [api, canManage]);

  const act = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const activeUsers = useMemo(
    () => users.filter((u) => u.status === "ACTIVE"),
    [users],
  );

  if (!canManage) return null;

  const visible = replies.filter((r) => {
    if (scope === SCOPE_ALL) return true;
    if (scope === SCOPE_COMMON) return !r.folderId;
    return r.folderId === scope;
  });

  const commonCount = replies.filter((r) => !r.folderId).length;

  /*
   * Перестановка соседей внутри показанного списка.
   *
   * Двумя запросами, а не одним: отдельного маршрута «переставить» нет, и
   * заводить его ради двух чисел значило бы расширять контракт API под нужды
   * одного экрана.
   */
  const move = (item: QuickReplyDto, delta: number): void => {
    const index = visible.findIndex((x) => x.id === item.id);
    const other = visible[index + delta];
    if (!other) return;

    void act(async () => {
      await api.updateQuickReply(item.id, { sortOrder: other.sortOrder });
      await api.updateQuickReply(other.id, { sortOrder: item.sortOrder });
    });
  };

  /** Папка, выбранная слева. У «Все» и «Общие» её нет — они не папки. */
  const openedFolder =
    scope === SCOPE_ALL || scope === SCOPE_COMMON
      ? null
      : (folders.find((f) => f.id === scope) ?? null);

  /** Папка окна настроек — всегда из свежего списка. */
  const folderSettings =
    folders.find((f) => f.id === folderSettingsId) ?? null;

  const scopeTitle =
    scope === SCOPE_ALL
      ? "Все заготовки"
      : scope === SCOPE_COMMON
        ? "Общие"
        : (folders.find((f) => f.id === scope)?.name ?? "Заготовки");

  return (
    <section className={cn(ADMIN_PANEL, "flex flex-col gap-4")}>
      <div>
        <h2 className={ADMIN_PANEL_TITLE}>Быстрые ответы</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          Заготовки текста, которые менеджер вставляет в поле ввода одним
          нажатием. В Telegram не уходят до отправки. Папка решает, кто заготовку
          увидит: общие видны всем, у папки — только тем, кому она выдана.
        </p>
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(180px,240px)_1fr]">
        {/* ── Папки ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Папки
            </p>
            {/*
              С подписью, а не голым плюсом.

              Значок без подписи в углу списка не читается как «завести папку»:
              человек с пустым списком видел строчку «папок нет» и не находил,
              чем это исправить. А без папки не появляется и выдача доступа —
              выдавать нечему.
            */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAddingFolder(true)}
              className="gap-1 px-2"
            >
              <PlusIcon className="size-3.5" />
              Папка
            </Button>
          </div>

          <ScopeRow
            active={scope === SCOPE_ALL}
            onClick={() => setScope(SCOPE_ALL)}
            count={replies.length}
          >
            Все заготовки
          </ScopeRow>

          <ScopeRow
            active={scope === SCOPE_COMMON}
            onClick={() => setScope(SCOPE_COMMON)}
            count={commonCount}
          >
            Общие
          </ScopeRow>

          {folders.map((folder) => (
            <ScopeRow
              key={folder.id}
              active={scope === folder.id}
              onClick={() => setScope(folder.id)}
              count={folder._count.replies}
              // Шестерёнка у папки: переименование, доступ и удаление — там,
              // где сама папка, а не в отдельной панели на другом конце экрана.
              onSettings={() => setFolderSettingsId(folder.id)}
              access={folder.access.length}
            >
              {folder.name}
            </ScopeRow>
          ))}

          {folders.length === 0 && (
            <div className="mt-1 rounded-xl border border-dashed border-border px-3 py-3">
              <p className="text-[12px] leading-relaxed text-muted-foreground">
                Папок нет — все заготовки видны всем.
              </p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                Доступ выдаётся папке, а не заготовке: заведите папку, и в ней
                можно будет указать, каким командам и сотрудникам она открыта.
              </p>
              <Button
                variant="brand"
                size="sm"
                onClick={() => setAddingFolder(true)}
                className="mt-2 gap-1.5"
              >
                <PlusIcon className="size-4" />
                Создать первую папку
              </Button>
            </div>
          )}
        </div>

        {/* ── Заготовки ─────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {scopeTitle} · {visible.length}
            </p>

            <div className="flex items-center gap-2">
              {/*
                Доступ к папке — отдельной кнопкой прямо здесь.

                Раньше единственным входом был значок на строке папки слева, и
                тот появлялся лишь при наведении. Выбрав папку, человек видел её
                заготовки и ни одного намёка на то, что доступом можно
                распорядиться. Кнопка стоит там, где на папку и смотрят.
              */}
              {openedFolder && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setFolderSettingsId(openedFolder.id)}
                  className="gap-1.5"
                >
                  <LockIcon className="size-4" />
                  Кому открыта
                  {openedFolder.access.length > 0 &&
                    ` · ${openedFolder.access.length}`}
                </Button>
              )}

              <Button
                variant="brand"
                size="sm"
                onClick={() => setEditing("new")}
                className="gap-1.5"
              >
                <PlusIcon className="size-4" />
                Заготовка
              </Button>
            </div>
          </div>

          {visible.length === 0 ? (
            <p className="rounded-xl border border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
              Здесь пусто. Нажмите «Заготовка», чтобы завести первую.
            </p>
          ) : (
            <ul className="flex list-none flex-col gap-1.5 p-0">
              {visible.map((item, index) => (
                <li
                  key={item.id}
                  className="flex items-start gap-2 rounded-xl border border-border bg-card px-3 py-2.5"
                >
                  <div className="flex flex-none flex-col">
                    <button
                      type="button"
                      aria-label="Выше"
                      title="Выше"
                      disabled={busy || index === 0}
                      onClick={() => move(item, -1)}
                      className="grid size-5 place-items-center rounded-md border-transparent bg-transparent text-muted-foreground transition-colors hover:bg-secondary hover:text-brand disabled:opacity-30"
                    >
                      <ChevronIcon className="size-3 rotate-180" />
                    </button>
                    <button
                      type="button"
                      aria-label="Ниже"
                      title="Ниже"
                      disabled={busy || index === visible.length - 1}
                      onClick={() => move(item, 1)}
                      className="grid size-5 place-items-center rounded-md border-transparent bg-transparent text-muted-foreground transition-colors hover:bg-secondary hover:text-brand disabled:opacity-30"
                    >
                      <ChevronIcon className="size-3" />
                    </button>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-heading">
                      {item.title}
                    </p>
                    {item.text && (
                      <p className="line-clamp-2 whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">
                        <RichText text={item.text} entities={item.entities ?? []} />
                      </p>
                    )}
                    {item.attachments && item.attachments.length > 0 && (
                      <div className="mt-1">
                        <QuickReplyThumbs replyId={item.id} attachments={item.attachments} size="xs" />
                      </div>
                    )}
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {item.folderName ?? "Общая"}
                      {item.author && ` · ${item.author}`}
                    </p>
                  </div>

                  <div className="flex flex-none items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      onClick={() => setEditing(item)}
                      aria-label={`Изменить «${item.title}»`}
                      title="Изменить"
                      className="text-muted-foreground"
                    >
                      <PencilIcon className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      onClick={() => {
                        // Заготовка общая для всей компании, удаление
                        // необратимо — подтверждение обязательно и называет
                        // последствие.
                        if (
                          window.confirm(
                            `Удалить «${item.title}»? Заготовка исчезнет у всех.`,
                          )
                        ) {
                          void act(() => api.deleteQuickReply(item.id));
                        }
                      }}
                      aria-label={`Удалить «${item.title}»`}
                      title="Удалить"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ReplyDialog
        value={editing}
        folders={folders}
        busy={busy}
        emojiAccountId={emojiAccountId}
        defaultFolderId={
          scope === SCOPE_ALL || scope === SCOPE_COMMON ? "" : scope
        }
        onClose={() => setEditing(null)}
        onSubmit={({ files, ...input }) =>
          void act(async () => {
            let id: string;
            if (editing && editing !== "new") {
              id = editing.id;
              if (Object.keys(input).length > 0) await api.updateQuickReply(id, input);
            } else {
              const created = await api.createQuickReply({
                title: input.title ?? "",
                text: input.text ?? "",
                folderId: input.folderId ?? null,
                ...(input.entities ? { entities: input.entities } : {}),
                sortOrder: replies.length,
              });
              id = created.id;
            }
            // Файлы уходят после записи заготовки: вложению нужен её id.
            for (const file of files) await api.uploadQuickReplyAttachment(id, file);
            setEditing(null);
          })
        }
        onRemoveAttachment={(replyId, attachmentId) =>
          void act(async () => {
            await api.deleteQuickReplyAttachment(replyId, attachmentId);
            // Окно остаётся открытым: обновляем заготовку в нём из свежего списка.
            const { items } = await api.listQuickReplies();
            const fresh = items.find((r) => r.id === replyId);
            if (fresh) setEditing(fresh);
          })
        }
      />

      <FolderDialog
        folder={folderSettings}
        teams={teams}
        users={activeUsers}
        busy={busy}
        onClose={() => setFolderSettingsId(null)}
        onRename={(name) =>
          void act(() =>
            api.updateQuickReplyFolder(folderSettingsId ?? "", { name }),
          )
        }
        onGrant={(subject) =>
          void act(() =>
            api.grantQuickReplyFolder(folderSettingsId ?? "", subject),
          )
        }
        onRevoke={(accessId) =>
          void act(() =>
            api.revokeQuickReplyFolder(folderSettingsId ?? "", accessId),
          )
        }
        onDelete={() =>
          void act(async () => {
            await api.deleteQuickReplyFolder(folderSettingsId ?? "");
            if (scope === folderSettingsId) setScope(SCOPE_ALL);
            setFolderSettingsId(null);
          })
        }
      />

      <NewFolderDialog
        open={addingFolder}
        busy={busy}
        onOpenChange={setAddingFolder}
        onSubmit={(name) =>
          void act(async () => {
            await api.createQuickReplyFolder({
              name,
              sortOrder: folders.length,
            });
            setAddingFolder(false);
          })
        }
      />
    </section>
  );
}

function ScopeRow({
  active,
  count,
  access,
  onClick,
  onSettings,
  children,
}: {
  active: boolean;
  count: number;
  access?: number;
  onClick: () => void;
  onSettings?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-xl px-1 transition-colors",
        active ? "bg-secondary" : "hover:bg-secondary/60",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className="flex min-w-0 flex-1 items-center gap-2 border-transparent bg-transparent px-2 py-2 text-left"
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-sm",
            active ? "font-medium text-brand" : "text-foreground",
          )}
        >
          {children}
        </span>
        <span className="flex-none text-[11px] text-muted-foreground">
          {count}
          {access !== undefined && access > 0 && ` · ${access} выдач`}
        </span>
      </button>

      {onSettings && (
        <button
          type="button"
          onClick={onSettings}
          aria-label="Настроить папку"
          title="Переименовать, выдать доступ, удалить"
          /*
            Видна ВСЕГДА, а не при наведении.

            Здесь стояло opacity-0 с проявлением по hover, и это была не мелочь:
            выдача доступа к папке открывается только отсюда, а на строке не
            было ни намёка, что кнопка существует. Человек смотрел на список папок и
            делал единственно возможный вывод — выдачи доступа в быстрых
            ответах нет.
          */
          className="grid size-6 flex-none place-items-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-brand hover:text-brand"
        >
          <PencilIcon className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Создание и правка заготовки.
 *
 * Одно окно на оба случая, но заголовок разный — именно его не хватало прежней
 * форме, где правка чужой заготовки выглядела как заведение новой.
 */
/** Что принимает редактор в виде файлов: то же, что и сервер. */
const ATTACHMENT_ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm";
const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

function ReplyDialog({
  value,
  folders,
  busy,
  emojiAccountId,
  defaultFolderId,
  onClose,
  onSubmit,
  onRemoveAttachment,
}: {
  value: QuickReplyDto | "new" | null;
  folders: readonly QuickReplyFolderDto[];
  busy: boolean;
  emojiAccountId: string | null;
  defaultFolderId: string;
  onClose: () => void;
  onSubmit: (input: {
    title?: string;
    text?: string;
    folderId?: string | null;
    entities?: TelegramTextEntityDto[];
    files: File[];
  }) => void;
  onRemoveAttachment: (replyId: string, attachmentId: string) => void;
}) {
  const existing = value && value !== "new" ? value : null;

  const [title, setTitle] = useState("");
  const [rich, setRich] = useState<RichValue>({ text: "", entities: [] });
  const [folderId, setFolderId] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const richRef = useRef<RichTextInputHandle>(null);

  const text = rich.text;
  const attachmentCount = (existing?.attachments?.length ?? 0) + files.length;

  /*
   * Поля наполняются при открытии.
   *
   * Ключ на содержимом окна пересоздаёт их для каждой заготовки — иначе в
   * следующей остались бы значения предыдущей, и правка молча переписала бы
   * чужой текст.
   */
  const key = existing?.id ?? (value === "new" ? "new" : "none");

  useEffect(() => {
    if (!value) return;
    setTitle(existing?.title ?? "");
    setFolderId(existing ? (existing.folderId ?? "") : defaultFolderId);
    setFiles([]);
    setFileError(null);
    const seeded: RichValue = {
      text: existing?.text ?? "",
      entities: existing?.entities ?? [],
    };
    setRich(seeded);
    // Наполняем поле визуально: премиальные эмодзи — картинками, остальное —
    // текстом. Ключ на DialogContent пересоздаёт узел под каждую заготовку,
    // поэтому таймер даёт contentEditable смонтироваться.
    requestAnimationFrame(() => richRef.current?.setValue(seeded));
  }, [value, existing, defaultFolderId]);

  // Заготовка без текста допустима, если есть вложение: «прайс картинкой».
  const ready = title.trim().length > 0 && (text.trim().length > 0 || attachmentCount > 0);

  return (
    <Dialog open={value !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent key={key}>
        <DialogHeader>
          <DialogTitle>
            {existing ? "Правка заготовки" : "Новая заготовка"}
          </DialogTitle>
          <DialogDescription>
            {existing
              ? "Заготовка общая: изменение увидят все, кому открыта её папка."
              : "Текст вставится в поле ввода — отправлять его всё равно вручную."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Название, например «Приветствие»"
            aria-label="Название заготовки"
            maxLength={80}
            className={FIELD}
          />
          {/*
            Поле с премиальными эмодзи: они видны картинками и добавляются
            пикером справа-снизу. Разметка сохраняется при правке — отдельный
            предпросмотр и предупреждение больше не нужны.
          */}
          <div className="relative min-h-[120px] rounded-xl border border-border-strong bg-card px-3 py-2.5 text-[13.5px] leading-relaxed focus-within:border-ring">
            <RichTextInput
              ref={richRef}
              value={rich}
              onChange={setRich}
              ariaLabel="Текст заготовки"
              placeholder={
                attachmentCount > 0
                  ? "Подпись к вложению (необязательно)"
                  : "Текст, который вставится в поле ввода"
              }
              className="min-h-[96px]"
            />
            <EmojiPicker
              onPick={(e) => richRef.current?.insertText(e)}
              onPickCustom={(id, emoji, src) =>
                richRef.current?.insertCustomEmoji(id, emoji, src)
              }
              accountId={emojiAccountId}
              direction="down"
            />
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Фото и видео
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {existing && existing.attachments && existing.attachments.length > 0 && (
                <QuickReplyThumbs
                  replyId={existing.id}
                  attachments={existing.attachments}
                  size="md"
                  disabled={busy}
                  onRemove={(attachmentId) => onRemoveAttachment(existing.id, attachmentId)}
                />
              )}
              {files.map((file, index) => (
                <span
                  key={`${file.name}-${index}`}
                  className="relative grid size-20 flex-none place-items-center overflow-hidden rounded-xl bg-muted p-1 text-center text-[10px] leading-tight text-muted-foreground"
                  title={file.name}
                >
                  <span className="line-clamp-3 break-all">{file.name}</span>
                  <button
                    type="button"
                    aria-label="Убрать файл"
                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    className="absolute right-0.5 top-0.5 grid size-5 place-items-center rounded-full bg-black/60 text-[11px] leading-none text-white hover:bg-destructive"
                  >
                    ×
                  </button>
                </span>
              ))}
              <label
                className={cn(
                  "grid size-20 cursor-pointer place-items-center rounded-xl border border-dashed border-border text-2xl text-muted-foreground transition-colors hover:border-brand hover:text-brand",
                  attachmentCount >= 10 && "pointer-events-none opacity-40",
                )}
                title="Добавить фото или видео"
              >
                +
                <input
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    setFileError(null);
                    const tooBig = picked.find((f) => f.size > ATTACHMENT_MAX_BYTES);
                    if (tooBig) {
                      setFileError(`«${tooBig.name}» больше 25 МБ`);
                      return;
                    }
                    setFiles((prev) => [...prev, ...picked].slice(0, 10 - (existing?.attachments?.length ?? 0)));
                  }}
                />
              </label>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {fileError ?? "JPEG, PNG, WebP, MP4, MOV, WebM — до 25 МБ. Первое вложение уйдёт с текстом как подписью."}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Папка
            </p>
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              aria-label="Папка заготовки"
              className={cn(FIELD, "w-full")}
            >
              <option value="">Общая — видят все</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button
            size="sm"
            disabled={busy || !ready}
            onClick={() => {
              // Пустая строка означает «общая заготовка», и на сервер она
              // должна уйти как null: uuid-схема отвергла бы пустую строку.
              const nextFolder = folderId || null;
              const trimmed = rich.text.trim();
              const entities = rich.entities;
              if (existing) {
                const textChanged = trimmed !== existing.text;
                const entitiesChanged =
                  JSON.stringify(entities) !== JSON.stringify(existing.entities ?? []);
                onSubmit({
                  ...(title.trim() !== existing.title ? { title: title.trim() } : {}),
                  ...(textChanged ? { text: trimmed } : {}),
                  // Разметку шлём вместе с текстом (сервер сбрасывает её при
                  // смене текста) или когда изменилась она сама.
                  ...(textChanged || entitiesChanged ? { entities } : {}),
                  ...(nextFolder !== (existing.folderId ?? null) ? { folderId: nextFolder } : {}),
                  files,
                });
              } else {
                onSubmit({
                  title: title.trim(),
                  text: trimmed,
                  ...(entities.length > 0 ? { entities } : {}),
                  folderId: nextFolder,
                  files,
                });
              }
            }}
          >
            {existing ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Папка: имя, доступ, удаление.
 *
 * Доступ выдаётся сотруднику, роли или команде — тем же способом, что и к
 * разделам рабочего места. Одинаковые вещи должны выглядеть одинаково: иначе
 * администратор заново разбирается в каждой.
 */
function FolderDialog({
  folder,
  teams,
  users,
  busy,
  onClose,
  onRename,
  onGrant,
  onRevoke,
  onDelete,
}: {
  folder: QuickReplyFolderDto | null;
  teams: readonly AccountFolderDto[];
  users: readonly AnyUser[];
  busy: boolean;
  onClose: () => void;
  onRename: (name: string) => void;
  onGrant: (subject: { userId?: string; role?: string; teamId?: string }) => void;
  onRevoke: (accessId: string) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(folder?.name ?? "");
    setConfirmDelete(false);
  }, [folder]);

  return (
    <Dialog open={folder !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent key={folder?.id ?? "none"}>
        <DialogHeader>
          <DialogTitle>Папка «{folder?.name}»</DialogTitle>
          <DialogDescription>
            Заготовки этой папки видят только те, кому она выдана.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-5">
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Название
            </p>
            <div className="flex items-center gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-label="Название папки"
                className={cn(FIELD, "min-w-0 flex-1")}
              />
              <Button
                size="sm"
                disabled={
                  busy || !name.trim() || name.trim() === folder?.name
                }
                onClick={() => onRename(name.trim())}
                className="flex-none"
              >
                Сохранить
              </Button>
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Кому открыта
            </p>
            {folder && (
              <AccessChips
                grants={folder.access.map((a) => ({
                  id: a.id,
                  userId: a.userId,
                  role: a.role,
                  teamId: a.teamId,
                }))}
                folders={teams}
                users={users}
                busy={busy}
                onGrant={(subject) =>
                  onGrant(
                    subject.kind === "user"
                      ? { userId: subject.id }
                      : subject.kind === "role"
                        ? { role: subject.id }
                        : { teamId: subject.id },
                  )
                }
                onRevoke={onRevoke}
              />
            )}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Удаление
            </p>
            {!confirmDelete ? (
              <>
                <p className="mb-2 text-[11px] leading-relaxed text-muted-foreground">
                  Заготовки при этом не пропадут — они станут общими и будут
                  видны всем.
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                  className="gap-1.5 text-muted-foreground hover:text-destructive"
                >
                  <TrashIcon className="size-4" />
                  Удалить папку
                </Button>
              </>
            ) : (
              <div className="flex flex-col gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-3">
                <p className="text-sm text-heading">
                  Удалить папку «{folder?.name}»?
                </p>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Её {folder?._count.replies ?? 0} заготовок станут общими, а
                  выданный доступ пропадёт.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy}
                    onClick={onDelete}
                  >
                    Да, удалить
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setConfirmDelete(false)}
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewFolderDialog({
  open,
  busy,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Новая папка</DialogTitle>
          <DialogDescription>
            Папка нужна, когда часть заготовок должна быть видна не всем. Кому
            её открыть — настроите сразу после создания.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Название папки"
            aria-label="Название папки"
            className={cn(FIELD, "w-full")}
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            size="sm"
            disabled={busy || !name.trim()}
            onClick={() => onSubmit(name.trim())}
          >
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
